import { Link, useNavigate, useLocation } from 'react-router-dom';
import Button from 'components/form/Button';
import { InputWithStyle } from 'components/form/Input';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { loginUser } from 'api/user';
import { getAccountType } from 'utils/token';

const LoginSchema = z.object({
  email: z.string().email({ message: 'Invalid email' }),
  password: z.string().min(8, 'password length should be a 8 or more'),
});

type LoginSchemaType = z.infer<typeof LoginSchema>;

const Login = () => {
  window.document.title = 'Login';
  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<LoginSchemaType>({
    resolver: zodResolver(LoginSchema),
    mode: 'onTouched',
  });

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submitFunction = async (data: LoginSchemaType) => {
    setLoading(true);
    try {
      const res = await loginUser(data);

      if (res?.data) {
        window.localStorage.setItem('token', res.data);
        if (pathname !== '/login') {
          return navigate(pathname);
        }
        const accountType = getAccountType(res.data);
        console.log(accountType);

        if (!accountType) return navigate('/');

        if (accountType === 'admin') return navigate('/admin');

        navigate('/dashboard');
      }
    } catch (error: any) {
      const { errors } = error.response.data;
      errors.map((err: { error: string }) => {
        return toast.error(err.error);
      });
      console.log(error);
    }
    setLoading(false);
  };

  const onSubmit = (data: LoginSchemaType) => {
    submitFunction(data);
  };
  return (
    <section className="flex h-screen">
      <div className="flex w-full justify-center">
        <div className="hidden h-screen basis-[65%] lg:block">
          <img
            src="/assets/images/chart.jpg"
            alt="card"
            className="h-full w-full object-cover brightness-75"
          />
        </div>
        <div className="my-auto basis-full px-10 md:basis-1/2 lg:basis-[35%]">
          <h3 className="mb-8 text-2xl font-medium">Login</h3>
          <form className="flex flex-col gap-y-5">
            <div className="flex flex-col gap-y-1">
              <label htmlFor="email" className="font-semibold">
                Email
              </label>
              <InputWithStyle
                startIcon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                    />
                  </svg>
                }
                name="email"
                placeholder="Enter email"
                type="email"
                control={control}
              />
            </div>
            <div className="flex flex-col gap-y-1">
              <div className="flex justify-between">
                <label htmlFor="email" className="font-semibold">
                  Password
                </label>
                <Link to="/reset-password" className="text-blue-500">
                  reset-password
                </Link>
              </div>
              <InputWithStyle
                startIcon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                }
                endIcon={
                  <Button type="button" onClick={() => setShow(!show)}>
                    {show ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-5 w-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-5 w-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    )}
                  </Button>
                }
                name="password"
                type={show ? 'text' : 'password'}
                placeholder="Enter password"
                control={control}
              />
            </div>

            <Button
              type="submit"
              onClick={handleSubmit(onSubmit)}
              className="rounded-md bg-blue-500 py-2 text-white"
              disabled={!isDirty || !isValid || loading}
            >
              Login
            </Button>
          </form>
          <br />
          <p className="my-2 mx-auto border"></p>
          <p>
            Don't have an account?{' '}
            <Link to="/register">
              <span className="text-blue-500">sign up</span>
            </Link>
          </p>
          <p className="mt-2">
            <Link to="/">
              <span className="text-blue-500">blockchain</span>
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Login;
